/**
 * Tiny always-on debug recorder (part of cmp.js).
 *
 * Records CMP events and every element the blocker touches into small capped
 * in-memory buffers from the very first moment, so the lazily loaded console
 * (cmp-console.js, opened via ?cmpconsole / #cmpconsole / localStorage
 * adcmp_console=1 / cmp.console()) can show what happened before it loaded.
 * No network, no storage, no DOM output here.
 */

import { DEBUG_GLOBAL } from "./debug_keys";
export { DEBUG_GLOBAL };

export type DbgState = "blocked" | "activated" | "allowed" | "unmatched" | "leaked";
/** How the element was detected/blocked. */
export type DbgVia =
  | "manual"      // publisher markup: type="text/plain" / data-cmp-* / data-cmp-src iframe
  | "hook"        // auto-block: appendChild/insertBefore interception
  | "observer"    // MutationObserver saw an element inserted without passing the hook (parser/innerHTML)
  | "preexisting";// already in the DOM when the CMP booted (before our blocker was active)

export type DbgElement = {
  id: number;
  tag: string;
  src: string;
  host: string;
  /** matched vendor key: GVL id ("755") or custom vendor key ("youtube") */
  vendor: string | null;
  purposes: string | null;
  via: DbgVia;
  state: DbgState;
  reason: string | null;
  /** first seen, ms since navigation start (performance.now()) */
  t: number;
  /** activated at (ms since navigation start) */
  ta?: number;
};

export type DbgEvent = { t: number; k: string; m: string; d?: unknown };

export type DbgStore = {
  v: string;
  t0: number;
  events: DbgEvent[];
  elements: DbgElement[];
  dropped: { events: number; elements: number };
  ctx?: () => Record<string, unknown>;
  listeners: Set<() => void>;
  /** >0 while the console itself calls CMP APIs (its own calls are not logged) */
  mute?: number;
};

export const MAX_EVENTS = 500;
export const MAX_ELEMENTS = 600;

declare const __CMP_VERSION__: string;
const version = typeof __CMP_VERSION__ === "string" ? __CMP_VERSION__ : "dev";

const now = () => (typeof performance !== "undefined" && performance.now ? Math.round(performance.now()) : 0);

function create(): DbgStore {
  return {
    v: version,
    t0: Date.now() - now(),
    events: [],
    elements: [],
    dropped: { events: 0, elements: 0 },
    listeners: new Set()
  };
}

const w = window as unknown as Record<string, unknown>;
export const store: DbgStore = (w[DEBUG_GLOBAL] as DbgStore) || create();
w[DEBUG_GLOBAL] = store;

let ids = new WeakMap<Element, number>();
let byId = new Map<number, DbgElement>();
let seq = 0;

function notify() {
  store.listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

export function log(k: string, m: string, d?: unknown) {
  if (store.mute) return;
  store.events.push(d === undefined ? { t: now(), k, m } : { t: now(), k, m, d });
  if (store.events.length > MAX_EVENTS) { store.events.shift(); store.dropped.events++; }
  notify();
}

export function hostOf(src: string): string {
  if (!src) return "";
  try { return new URL(src, location.href).hostname; } catch { return ""; }
}

export function srcOf(el: Element): string {
  return el.getAttribute("data-cmp-src") || el.getAttribute("src") || el.getAttribute("href") || "";
}

/** Create or update the record for an element. Returns the record. */
export function track(el: Element, patch: Partial<DbgElement> & { state: DbgState; via?: DbgVia }): DbgElement {
  let id = ids.get(el);
  let rec = id ? byId.get(id) : undefined;
  const isNew = !rec;
  if (!rec) {
    id = ++seq;
    ids.set(el, id);
    const src = srcOf(el);
    rec = {
      id, tag: el.tagName, src, host: hostOf(src),
      vendor: null, purposes: el.getAttribute("data-cmp-purposes"),
      via: patch.via || "manual", state: patch.state, reason: null, t: now()
    };
    byId.set(id, rec);
    store.elements.push(rec);
    if (store.elements.length > MAX_ELEMENTS) {
      const old = store.elements.shift()!;
      byId.delete(old.id);
      store.dropped.elements++;
    }
  }
  const prev = rec.state;
  Object.assign(rec, patch);
  if (!rec.src) { rec.src = srcOf(el); rec.host = hostOf(rec.src); }
  if (patch.state === "activated" && prev !== "activated") rec.ta = now();
  if (isNew || prev !== rec.state) {
    log(rec.state === "activated" ? "unblock" : rec.state === "blocked" ? "block" : "element",
      `${rec.tag.toLowerCase()} ${rec.state}${rec.host ? " " + rec.host : ""}${rec.vendor ? " → " + rec.vendor : ""}`,
      { id: rec.id, via: rec.via, reason: rec.reason });
  }
  notify();
  return rec;
}

/** An element was replaced by a clone (script re-creation) — keep the same record. */
export function alias(from: Element, to: Element) {
  const id = ids.get(from);
  if (id) ids.set(to, id);
}

export function isTracked(el: Element) { return ids.has(el); }

/** Test helper. */
export function resetDebug() {
  store.events.length = 0;
  store.elements.length = 0;
  store.dropped.events = 0;
  store.dropped.elements = 0;
  ids = new WeakMap();
  byId = new Map();
  seq = 0;
}
